// The decision-22 surface: the disclosure that we are keeping this person's
// answers, the identity that makes keeping them possible, and — for an organic
// visitor — the link back.
//
// TWO THINGS HERE ARE LOAD-BEARING.
//
// The disclosure renders BEFORE the first save, never after it. Half of the
// obligation is telling somebody their partial financial answers are being
// stored; the other half is telling them while they can still decide not to.
//
// The resume link is rendered VERBATIM, exactly once, at the moment the draft
// is created. It is the only time that token is ever emitted to anyone and
// there is no email fallback by design, so a version of this component that
// forgets to render it strands the visitor permanently — and does so silently,
// because everything else about the save still worked.
//
// In ASSISTED mode the whole card is suppressed. The invite behind an assisted
// draft is minted with its email deliberately suppressed (the client is in the
// room), so promising them a link back would promise something nobody sends.

import { useState } from "react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import {
  SAVE_PROGRESS_DISCLOSURE,
  SAVE_PROGRESS_INVITED,
  SAVE_PROGRESS_LINK_WARNING,
  SAVE_PROGRESS_RELOAD_ACTION,
  SAVE_PROGRESS_SAVE_ACTION,
  SAVE_PROGRESS_SAVED_BODY,
  SAVE_PROGRESS_SAVED_NO_LINK,
  SAVE_PROGRESS_SAVED_TITLE,
  SAVE_PROGRESS_TITLE,
} from "./prequalContent";
import type { PrequalDraftIdentity, PrequalPersistenceMode } from "./prequalDraftAdapter";
import { isIdentityComplete } from "./prequalDraftAdapter";
import type { PrequalDraftSession } from "./usePrequalDraftSession";

const CARD_SHELL =
  "rounded-[14px] border border-line bg-white-pure shadow-[0_10px_34px_rgba(20,20,20,0.07)]";

export interface PrequalSaveProgressCardProps {
  mode: PrequalPersistenceMode;
  /** True when the visitor arrived through a tracked invite: identity comes
   *  from the invite, and the way back is the link they were emailed. */
  invited: boolean;
  /** False on the finalize step, where the form itself captures identity —
   *  two live copies of the same three fields on one screen is a bug report
   *  waiting to happen. */
  showIdentity: boolean;
  identity: PrequalDraftIdentity;
  onIdentityChange: (patch: Partial<PrequalDraftIdentity>) => void;
  onSave: () => void;
  session: PrequalDraftSession;
}

export function PrequalSaveProgressCard({
  mode,
  invited,
  showIdentity,
  identity,
  onIdentityChange,
  onSave,
  session,
}: PrequalSaveProgressCardProps) {
  const [copied, setCopied] = useState(false);

  if (mode === "assisted") return null;

  const { saved, resumeUrl, conflict, error, saving } = session;

  async function copyLink(url: string) {
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
    } catch {
      // No clipboard permission — the link is selectable on screen either way.
    }
  }

  return (
    <div className={cn(CARD_SHELL, "p-space-4")} data-testid="prequal-save-progress">
      <h2 className="text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep m-0">
        {saved ? SAVE_PROGRESS_SAVED_TITLE : SAVE_PROGRESS_TITLE}
      </h2>

      {invited ? (
        <p className="text-caption text-ink-mid mt-space-2 mb-0">{SAVE_PROGRESS_INVITED}</p>
      ) : saved ? (
        <>
          <p className="text-caption text-ink-mid mt-space-2 mb-0">
            {resumeUrl ? SAVE_PROGRESS_SAVED_BODY : SAVE_PROGRESS_SAVED_NO_LINK}
          </p>
          {resumeUrl && (
            <div className="mt-space-3 flex flex-col gap-space-2">
              {/* Verbatim, never assembled here: the query-parameter name is
                  what selects the store on the way back in. */}
              <a
                href={resumeUrl}
                className="text-caption text-gold-shadow underline break-all"
                data-testid="prequal-resume-link"
              >
                {resumeUrl}
              </a>
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void copyLink(resumeUrl)}
                  data-testid="prequal-resume-copy"
                >
                  {copied ? "Copied" : "Copy link"}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="text-caption text-ink-mid mt-space-2 mb-0">{SAVE_PROGRESS_DISCLOSURE}</p>
          <p className="text-caption text-ink-faint mt-space-2 mb-0">
            {SAVE_PROGRESS_LINK_WARNING}
          </p>

          {showIdentity && (
            <div className="mt-space-3 flex flex-col gap-space-2">
              <FormField label="First name">
                <Input
                  value={identity.firstName}
                  onChange={(e) => onIdentityChange({ firstName: e.target.value })}
                  data-testid="prequal-save-first-name"
                />
              </FormField>
              <FormField label="Last name">
                <Input
                  value={identity.lastName}
                  onChange={(e) => onIdentityChange({ lastName: e.target.value })}
                  data-testid="prequal-save-last-name"
                />
              </FormField>
              <FormField label="Email">
                <Input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={identity.email}
                  onChange={(e) => onIdentityChange({ email: e.target.value })}
                  data-testid="prequal-save-email"
                />
              </FormField>
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={saving || !isIdentityComplete(identity)}
                  onClick={onSave}
                  data-testid="prequal-save-submit"
                >
                  {SAVE_PROGRESS_SAVE_ACTION}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {conflict && (
        <div className="mt-space-3" data-testid="prequal-save-conflict">
          <p className="text-caption text-status-error m-0" role="alert">
            {error}
          </p>
          <div className="mt-space-2">
            <Button type="button" variant="secondary" onClick={session.reload}>
              {SAVE_PROGRESS_RELOAD_ACTION}
            </Button>
          </div>
        </div>
      )}

      {!conflict && error && (
        <p className="text-caption text-ink-faint mt-space-2 mb-0" data-testid="prequal-save-error">
          {error}
        </p>
      )}
    </div>
  );
}
